import { Container, Flex, Icon, Link, Text } from "@chakra-ui/react";
import { Link as GatsbyLink } from "gatsby";
import { useEffect, useState } from "react";
import { ReactComponent as ESCDA2026Logo } from "src/assets/icons/ESCDA2026.svg";
import { useIsESCDA2026Enabled } from "src/hooks/useIsESCDA2026Enabled";

const LINK_URL = "/facturation-electronique";
const TARGET_DATE = new Date("2026-09-01T00:00:00+02:00").getTime(); //France Timezone Target Date Set

const pad = (value: number) => String(value).padStart(2, "0");

const getTimeParts = () => {
  const diff = Math.max(0, TARGET_DATE - Date.now());

  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
};

const useCountdown = () => {
  const [time, setTime] = useState(getTimeParts);

  useEffect(() => {
    if (TARGET_DATE <= Date.now()) return;

    const interval = setInterval(() => {
      setTime(getTimeParts());

      if (TARGET_DATE <= Date.now()) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isFinished = TARGET_DATE <= Date.now();

  return { time, isFinished };
};

const CountdownTimer = ({ time }: { time: ReturnType<typeof getTimeParts> }) => {
  const units: Array<[number, string]> = [
    [time.days, "Jours"],
    [time.hours, "Hrs"],
    [time.minutes, "Min"],
    [time.seconds, "Sec"],
  ];

  return (
    <Flex gap="space-4" alignItems="flex-end">
      {units.map(([value, label]) => (
        <Flex
          key={label}
          direction="column"
          alignItems="center"
          backgroundColor="accounting-green-900"
          borderRadius="6px"
          paddingY="space-4"
          width="42px"
        >
          <Text
            fontSize={{ base: "font-14", lg: "font-16" }}
            fontWeight="bold"
            color="monochrome-white"
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            {pad(value)}
          </Text>
          <Text
            fontSize="8px"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="0.04em"
            color="accounting-green-300"
          >
            {label}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

export const ESCDA2026Banner = () => {
  const { isESCDA2026Enabled } = useIsESCDA2026Enabled();
  const { time, isFinished } = useCountdown();

  if (!isESCDA2026Enabled) {
    return null;
  }

  return (
    <Container
      alignItems="center"
      backgroundColor="primary-green"
      display="flex"
      flexDirection={{ base: "column", sm: "row" }}
      gap={{ base: "space-8", lg: "space-16" }}
      justifyContent="center"
      paddingX={{ base: "space-16", lg: 0 }}
      paddingY={12.33}
      width="100%"
    >
      <Flex
        alignItems="center"
        gap={{ base: "space-9", lg: "space-16" }}
      >
        <Icon
          alt="ESCDA2026"
          as={ESCDA2026Logo}
          height="auto"
          width={{ base: "16", lg: 85 }}
        />

        {!isFinished && <CountdownTimer time={time} />}
      </Flex>

      <Text
        fontSize={{ base: "font-12", lg: "font-16" }}
        fontWeight="medium"
        color="monochrome-white"
        textAlign={{ base: "center", sm: "left" }}
      >
       Facturation électronique obligatoire {isFinished ? "depuis le" : "le"} 1er septembre{"  "}
        <Link
          as={GatsbyLink}
          to={LINK_URL}
          textDecorationLine="underline"
          color="monochrome-white"
          paddingLeft={{ base: 0, sm: "space-8", lg: "space-12" }}
          whiteSpace="nowrap"
          fontWeight="normal"
          style={{
            textDecorationSkipInk: "none",
            textUnderlinePosition: "from-font",
          }}
        >
          Activer ma PA gratuite
        </Link>
      </Text>
    </Container>
  );
};
